import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Collapse, Form } from "antd";
import { useTranslation } from "react-i18next";
import { DynamicFormField } from "./DynamicFormField";
import { useFormItemType } from "./FormItemTypeContext";
import type { DynamicFormSectionProps } from "./types";
import { useResponsive } from "../../hooks";
import { getFieldVisibilityRules } from "../../config/fieldVisibilityRules";

/**
 * Form section component that renders a group of fields in a card
 * Supports Aras Responsive Form layout properties (title, bordered, columns up to 4)
 * Adapts to mobile/tablet screens automatically
 */
export const DynamicFormSection: React.FC<DynamicFormSectionProps> = ({
  config,
  fields,
  form,
  disabled = false,
  readOnly = false,
  customRenderers,
  defaultCollapsed = false,
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();

  // Get number of columns - responsive override
  const configColumns = config.columns || 2;
  // Mobile: always 1 column, Tablet: max 2 columns, Desktop: use config
  const columns = isMobile
    ? 1
    : isTablet
      ? Math.min(configColumns, 2)
      : configColumns;
  const colSpan = Math.floor(24 / columns);

  // Filter fields that belong to this section
  const sectionFields = useMemo(
    () => fields.filter((field) => config.fields.includes(field.mappedName)),
    [fields, config.fields],
  );

  // Sort fields by their position in config.fields array
  const sortedFields = useMemo(
    () =>
      [...sectionFields].sort((a, b) => {
        const indexA = config.fields.indexOf(a.mappedName);
        const indexB = config.fields.indexOf(b.mappedName);
        return indexA - indexB;
      }),
    [sectionFields, config.fields],
  );

  // Get title - prefer direct title from Aras, otherwise use i18n key
  // For Responsive Form sections, titleKey is empty so we only use config.title
  const title = config.title || (config.titleKey ? t(config.titleKey) : "");

  // Track expanded state for collapsible groupboxes
  const [activeKey, setActiveKey] = useState<string[]>(
    defaultCollapsed ? [] : ["main"],
  );

  // Build a map of field name to field metadata for quick lookup
  const fieldMap = useMemo(
    () => new Map(fields.map((f) => [f.mappedName, f])),
    [fields],
  );

  // Conditional visibility rules — registry keyed by ItemType.
  const itemTypeName = useFormItemType();
  const visibilityRules = useMemo(
    () => getFieldVisibilityRules(itemTypeName),
    [itemTypeName],
  );

  // Union of fields any rule reads. Stable per ItemType (rules are static config),
  // so the count of subscriptions doesn't change at runtime for a mounted form.
  const watchedNames = useMemo(
    () =>
      Array.from(
        new Set(Object.values(visibilityRules).flatMap((r) => r.dependsOn)),
      ).sort(),
    [visibilityRules],
  );

  // Subscribe with a stable string key so the section re-renders ONLY when one of
  // the watched fields actually changes — not on every keystroke in unrelated fields.
  // AntD's useWatch shallow-compares selector results; returning a primitive avoids
  // the new-object-every-render issue of `(values) => values`.
  const watchedKey =
    Form.useWatch(
      (values) =>
        watchedNames
          .map(
            (n) =>
              `${n}=${JSON.stringify(
                (values as Record<string, unknown> | undefined)?.[n] ?? null,
              )}`,
          )
          .join("|"),
      form,
    ) ?? "";

  // Read fresh values via form when watchedKey changes (it encodes the snapshot).
  const watchedValues = useMemo<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    watchedNames.forEach((n) => {
      v[n] = form.getFieldValue(n);
    });
    return v;
    // watchedKey is the trigger; getFieldValue reads fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedKey, watchedNames, form]);

  const isFieldHidden = (fieldName: string): boolean => {
    const rule = visibilityRules[fieldName];
    if (!rule) return false;
    return !rule.isVisible(watchedValues);
  };

  // Clear values of hidden fields so they aren't submitted with stale data.
  // Only fields that this section owns (sectionFields) are managed here to
  // avoid multiple sections fighting over the same field.
  // Skip in readOnly (show) mode — we must not mutate the displayed snapshot.
  useEffect(() => {
    if (readOnly) return;
    sectionFields.forEach((field) => {
      const rule = visibilityRules[field.mappedName];
      if (!rule) return;
      if (
        !rule.isVisible(watchedValues) &&
        form.getFieldValue(field.mappedName) !== undefined
      ) {
        form.setFieldValue(field.mappedName, undefined);
      }
    });
  }, [sectionFields, visibilityRules, watchedValues, form, readOnly]);

  // Sanity warning: flag drift between this registry and Aras List source-of-truth.
  // If a rule expects specific list values that aren't present in the loaded options,
  // the rule will silently never match — log so it surfaces in dev/QA before users hit it.
  useEffect(() => {
    Object.entries(visibilityRules).forEach(([fieldName, rule]) => {
      if (!rule.expectedListValues) return;
      Object.entries(rule.expectedListValues).forEach(
        ([depFieldName, expected]) => {
          const depField = fieldMap.get(depFieldName);
          if (!depField || !depField.options) return;
          const optionValues = new Set(
            depField.options.map((o) => String(o.value)),
          );
          const missing = expected.filter((v) => !optionValues.has(v));
          if (missing.length > 0) {
            console.warn(
              `[fieldVisibilityRules] Rule for "${fieldName}" expects list values [${missing
                .map((m) => `"${m}"`)
                .join(
                  ", ",
                )}] on "${depFieldName}" but they are not present in Aras list options. Update the registry or the Aras List.`,
            );
          }
        },
      );
    });
  }, [visibilityRules, fieldMap]);

  // If no fields match, don't render the section
  if (sectionFields.length === 0) {
    return null;
  }

  // Helper to render a single field
  const renderField = (fieldName: string) => {
    const field = fieldMap.get(fieldName);
    if (!field) return null;
    if (isFieldHidden(fieldName)) return null;
    // Merge section-level disabled with per-field disabled from Aras form layout
    const fieldDisabled = disabled || field.isDisabledByForm === true;
    const CustomRenderer = customRenderers?.[field.mappedName];
    return CustomRenderer ? (
      <CustomRenderer
        key={field.mappedName}
        field={field}
        form={form}
        disabled={fieldDisabled}
        readOnly={readOnly}
      />
    ) : (
      <DynamicFormField
        key={field.mappedName}
        field={field}
        form={form}
        disabled={fieldDisabled}
        readOnly={readOnly}
      />
    );
  };

  // Responsive gutter - less on mobile
  const gutter = isMobile ? 0 : 16;

  // Render fields - shared between Card and non-Card layouts
  const renderFieldsContent = () => {
    // If we have rows (from Responsive Form with mixed pattern), render row by row
    if (config.rows && config.rows.length > 0) {
      return (
        <>
          {config.rows.map((rowFields, rowIndex) => {
            // Drop rule-hidden positions so the row reflows to fill the gap.
            // Original empty positions ("") are kept — they're a deliberate Aras
            // layout choice and must preserve cross-row grid alignment.
            const effectiveRowFields = rowFields.filter(
              (name) => name === "" || !isFieldHidden(name),
            );

            // If every field in the row is rule-hidden, omit the row entirely.
            if (effectiveRowFields.length === 0) return null;

            // Calculate column span from the effective (post-collapse) row.
            const fieldsInRow = effectiveRowFields.length;
            const nonEmptyCount = effectiveRowFields.filter(
              (f) => f !== "",
            ).length;

            // Responsive row column span
            let rowColSpan: number;
            if (isMobile) {
              rowColSpan = 24; // Full width on mobile
            } else if (nonEmptyCount === 1 && fieldsInRow === 1) {
              rowColSpan = 24;
            } else if (isTablet) {
              // On tablet, max 2 columns per row
              rowColSpan = Math.max(
                12,
                Math.floor(24 / Math.min(fieldsInRow, 2)),
              );
            } else {
              rowColSpan = Math.floor(24 / fieldsInRow);
            }

            return (
              <Row key={`row-${rowIndex}`} gutter={gutter}>
                {effectiveRowFields.map((fieldName, colIndex) => (
                  <Col
                    key={fieldName || `empty-${rowIndex}-${colIndex}`}
                    xs={24}
                    sm={24}
                    md={nonEmptyCount === 1 ? 24 : 12}
                    lg={rowColSpan}
                  >
                    {/* Empty position from Aras layout → render nothing but keep Col for alignment. */}
                    {fieldName ? renderField(fieldName) : null}
                  </Col>
                ))}
              </Row>
            );
          })}
        </>
      );
    }

    // If we have columnFields (from Responsive Form), render by column structure
    if (config.columnFields && config.columnFields.length > 0) {
      return (
        <Row gutter={gutter}>
          {config.columnFields.map((columnFieldNames, colIndex) => (
            <Col
              key={`col-${colIndex}`}
              xs={24}
              sm={24}
              md={isTablet ? Math.max(12, colSpan) : colSpan}
              lg={colSpan}
            >
              {columnFieldNames.map((fieldName) => renderField(fieldName))}
            </Col>
          ))}
        </Row>
      );
    }

    // Fallback: render fields in order (static config)
    return (
      <Row gutter={gutter}>
        {sortedFields.map((field) => (
          <Col
            key={field.mappedName}
            xs={24}
            sm={24}
            md={isTablet ? Math.max(12, colSpan) : colSpan}
            lg={colSpan}
          >
            {renderField(field.mappedName)}
          </Col>
        ))}
      </Row>
    );
  };

  // For Responsive Form columnsets without title/border: render just the content
  // This matches Aras display where columnsets are invisible layout containers
  // Use == null to check for both null and undefined (API may return null)
  if (!title && config.bordered == null) {
    return <>{renderFieldsContent()}</>;
  }

  // For bordered sections (group boxes) or sections with titles: use Collapse
  // This allows auto-collapsing from 2nd groupbox onwards
  const collapseStyle: React.CSSProperties = {
    marginBottom: isMobile ? 12 : 16,
    background: "transparent",
  };

  // Style for non-bordered sections (invisible groupbox with just title)
  const nonBorderedStyle: React.CSSProperties = {
    marginBottom: isMobile ? 12 : 16,
    border: "none",
    boxShadow: "none",
  };

  // Use Collapse for collapsible groupboxes
  return (
    <Collapse
      activeKey={activeKey}
      onChange={(keys) => setActiveKey(keys as string[])}
      style={config.bordered === false ? nonBorderedStyle : collapseStyle}
      size={isMobile ? "small" : "middle"}
      items={[
        {
          key: "main",
          label: title || undefined,
          children: renderFieldsContent(),
        },
      ]}
    />
  );
};
